import ApiService from '../common/api.service'

class UserService extends ApiService {

  constructor ($log, $http, $window, $rootScope, Localization, I18n, $state, BufferFactory, StoreTypes, MyRenaultErrorCodes) {
    'ngInject'
    super($log, $http, $window, $rootScope, Localization, I18n)

    this.$state = $state
    this.BufferFactory = BufferFactory
    this.StoreTypes = StoreTypes
    this.type = Localization.getLocalizedAppConfig().type
    this.myRenaultErrorCodes = MyRenaultErrorCodes
    this.myRenaultErrorUrl = Localization.getLocalizedAppConfig().errorUrl
    this.shopUrl = Localization.getLocalizedAppConfig().shopUrl

    // Refresh token when 401 status is received from API
    $rootScope.$on('user:unauthorized', (event, rejection) => {
      this.$log.debug('user:unauthorized received')
      return this.refresh()
      .then(
        () => {
          this.$log.debug('refresh succeeded')
          return BufferFactory.retryAll()
        },
        (err) => {
          this.$log.debug('refresh failed')
          return this.resetBufferAndLogout(err)
        })
    })

    $rootScope.$on('service:stopped', () => {
      this.$log.debug('service stopped')
      return this.BufferFactory.rejectAll()
      .then(() => {
        this.$log.debug('buffer rejected')
        this.clearSession()
        return this.$state.go('home')
      })
    })

    $rootScope.$on('service:maintenance', () => {
      this.$log.debug('service in maintenance')
      return this.BufferFactory.rejectAll()
        .then(() => {
          this.$log.debug('buffer rejected')
          this.clearSession()
          return this.$state.go('maintenance')
        })
    })
  }

  /**
   * Login
   * @param {object} credentials User credentials (email/password)
   * @return {Promise} Promise of the login
   */
  login (credentials) {
    // Empty cache before login
    this.$rootScope.$broadcast('user:logout')
    return this.post('/login', credentials, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }).then((res) => {
      const {access_token, token_type, expires_in, refresh_token} = res.data
      const {user} = res.data
      this.$log.debug('Storing token into session storage', access_token)
      this.$window.sessionStorage.token = JSON.stringify({
        access_token, token_type, expires_in, refresh_token
      })
      this.$window.sessionStorage.captchaNeeded = false
      this.$log.debug('Storing user into session storage', user)
      this.$window.sessionStorage.user = JSON.stringify(user)
      this.$rootScope.$broadcast('user:login', user)
      return Promise.resolve(user)
    }, (err) => {
      this.$window.sessionStorage.captchaNeeded = (err.data.error === 'Captcha needed')
      return Promise.reject(err)
    })
  }

  /**
   * Refresh user token
   * @return {Promise} Promise of the refresh
   */
  refresh () {
    const token = this.getCurrentToken()
    if (token) {
      return this.post('/refresh', {}, {
        params: {
          'refreshToken': token.refresh_token
        }
      }).then((res) => {
        const {access_token, token_type, expires_in, refresh_token} = res.data
        this.$log.debug('Storing token into local storage', access_token)
        this.$window.sessionStorage.token = JSON.stringify({
          access_token, token_type, expires_in, refresh_token
        })
        return Promise.resolve()
      })
    }
    return Promise.reject()
  }

  /**
   * Logout user
   * @return {Promise} Promise of the logout
   */
  logout () {
    this.$log.debug('logout')
    return this.get('/logout').then((res) => {
      this.$log.debug('logout succeeded')
      this.clearSession()
      return Promise.resolve(res)
    }, (err) => {
      this.$log.debug('logout failed')
      this.clearSession()
      return Promise.reject(err)
    })
  }

  /**
   * Remove session and cache information and broadcast logout event
   */
  clearSession () {
    this.$log.debug('clearing session...')
    this.$window.sessionStorage.removeItem('user')
    this.$window.sessionStorage.removeItem('token')
    this.$window.localStorage.removeItem('token')
    this.$window.sessionStorage.removeItem('vin')
    this.initCache()
    this._fetchingCurrentUser = null
    this.BufferFactory.rejectAll()
    .then(() => {
      this.$rootScope.$broadcast('user:logout')
      this.$log.debug('session cleared')
    })
  }

  /**
   * Get current logged user.
   * @param {boolean} useCache Try to get user form cache
   * @return {Promise} Promise of the current user
   */
  getCurrentUser (useCache = true) {
    const cacheKey = 'current_user'
    const inCache = this.getFromCache(cacheKey)
    if (useCache && inCache) {
      // Get current user form cache
      return Promise.resolve(inCache)
    }
    if (this._fetchingCurrentUser) {
      this.$log.debug('Already fetching current user. Waiting current resolution.')
      return this._fetchingCurrentUser
    }

    this._fetchingCurrentUser = this.get('/users/current')
      .then((res) => {
        this.putInCache(cacheKey, res.data)
        this.$log.info('Fetched current user', res.data)
        this._fetchingCurrentUser = null
        return Promise.resolve(res.data)
      })
    return this._fetchingCurrentUser
  }

  setCurrentUserCountryOfResidence (country) {
    const user = JSON.parse(this.$window.sessionStorage.getItem('user'))
    user.customer.countryOfResidence = country
    this.$window.sessionStorage.setItem('user', JSON.stringify(user))
    return user
  }

  getCurrentCompany () {
    const { user } = this.$window.sessionStorage
    return JSON.parse(user).company
  }

  setCurrentCompany (company) {
    const user = JSON.parse(this.$window.sessionStorage.getItem('user'))
    user.company = company
    this.$window.sessionStorage.setItem('user', JSON.stringify(user))
  }

  getPspType () {
    const { user } = this.$window.sessionStorage
    return JSON.parse(user).pspType
  }

  getCountryOfResidence () {
    const { user } = this.$window.sessionStorage
    return JSON.parse(user).customer.countryOfResidence
  }

  /**
   * Get current token from the session storage
   * @return {Object} token
   */
  getCurrentToken () {
    const { token } = this.$window.sessionStorage
    return token ? JSON.parse(token) : null
  }

  getCaptchaNeeded () {
    return this.$window.sessionStorage.captchaNeeded === 'true'
  }

  resetPassword (email) {
    // TODO check usage and API
    return this.post('/public/users/password', email, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  changePassword (oldPassword, newPassword) {
    return this.put('/users/current/password/update', {oldPassword, newPassword}).then((res) => {
      return Promise.resolve(res)
    })
  }

  putCountryOfResidence (newCountry) {
    return this.put(`/users/current/country`, null, {
      params: {
        'country': newCountry
      }
    }).then((res) => {
      Promise.resolve(res)
    })
  }

  /**
   * Update current logged user.
   * @param {Object} payload Update payload
   * @return {Promise} Promise of the updated user
   */
  update (payload) {
    return this.getCurrentUser()
      .then((user) => {
        const update = Object.assign({}, user, payload)
        this.$log.info('Updating user:', update)
        return this.put('/users/current', update)
      })
      // TODO Update user endpoint should return the updated user
      .then(() => this.getCurrentUser(false))
  }

  resetPasswordConfirmation (email, token) {
    // TODO check usage and API
    return this.post('/public/users/password/confirm', {email, token}, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  /**
   * Get cards of the current user.
   * @return {Promise} wallet of the current user
   */
  getWallet () {
    return this.get('/users/current/wallet')
      .then((res) => Promise.resolve(res.data))
  }

  /**
   * Remove a card form the current user wallet.
   * @param {String} id Card ID
   * @param {String} psptype PSP type
   * @return {Promise} the deletion result
   */
  removeCardFromWallet (id, psptype) {
    return this.delete(`/users/current/wallet/cards/${id}`, {
      params: {psptype}
    })
    // TODO Remove card from wallet endpoint should return the updated wallet
    .then(() => this.getWallet())
  }

  countryConcernedByWithdrawalRights (country) {
    // TODO check usage and API
    return this.get(`/countries/${country}/withdrawal`)
    .then((res) => Promise.resolve(res.data))
  }

  isEuropeanUnionCountry (country) {
    // TODO check usage and API
    return this.get(`/countries/${country}/europe`)
    .then((res) => Promise.resolve(res.data))
  }

  /**
   * Calls rejectAll method of the buffer factory and the logout method
   * @param {Object} the optional error
   */
  resetBufferAndLogout (err) {
    return this.BufferFactory.rejectAll(err)
    .then(() => {
      return this.logout()
      .then(() => {
        return this.redirectWhenTokenExpired()
      }, () => {
        return this.redirectWhenTokenExpired()
      })
    })
  }

  redirectWhenTokenExpired () {
    this.$log.error('Session expired! Redirecting depending on type...')
    if (this.type === this.StoreTypes.HELIOS ||
      this.type === this.StoreTypes.ONE ||
      this.type === this.StoreTypes.MYRENAULT) {
      this.$window.top.location.href = this.myRenaultErrorUrl + this.myRenaultErrorCodes.SESSION_TIMEOUT
      return Promise.reject()
    } else if (this.type === this.StoreTypes.RSITE) {
      const REDIRECT_RSITE = 'redirectRsite'
      this.$window.localStorage[REDIRECT_RSITE] = 'true'
      this.$window.top.location.href = this.shopUrl
      return Promise.reject()
    } else {
      return this.$state.go('authentication')
    }
  }

  isPasswordExpired (lastPasswordUpdate, passwordExpirationDay) {
    if (lastPasswordUpdate == null) {
      return true
    }
    let currTime = Date.now()
    let passwordUpdateTime = Date.parse(lastPasswordUpdate)
    const dayDiff = parseInt((currTime - passwordUpdateTime) / (24 * 3600 * 1000))
    return dayDiff >= passwordExpirationDay
  }

  checkPassword (password) {
    if (password && password.length >= 8 && password.length <= 32) {
      return this.get(`/public/password/validate`, {
        params: {password}
      })
        .then((res) => res.data === 'true' ? Promise.resolve(true) : Promise.reject(false))
    }
    return Promise.resolve(true)
  }
}
export default UserService



// WEBPACK FOOTER //
// ./src/app/services/user/user.service.js